import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { CheckCircle, AlertCircle, Settings } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export const StripeConfigurationPanel = () => {
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [configurationStatus, setConfigurationStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const { toast } = useToast();

  const handleConfigureStripeTax = async () => {
    setIsConfiguring(true);
    setConfigurationStatus('idle');
    setErrorMessage('');

    try {
      const { data, error } = await supabase.functions.invoke('setup-stripe-tax', {
        body: {},
      });

      if (error) {
        throw error;
      }

      if (data.success) {
        setConfigurationStatus('success');
        toast({
          title: "Configuración completada",
          description: "Los impuestos automáticos han sido configurados en Stripe.",
        });
      } else {
        throw new Error(data.error || 'Error desconocido');
      }
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Error desconocido';
      setConfigurationStatus('error');
      setErrorMessage(message);
      toast({
        title: "Error en la configuración",
        description: message,
        variant: "destructive",
      });
    } finally {
      setIsConfiguring(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Settings className="h-5 w-5" />
          Configuración de Impuestos Stripe
        </CardTitle>
        <CardDescription>
          Configura automáticamente los productos y precios de Stripe con cálculo de impuestos.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {configurationStatus === 'success' && (
          <Alert>
            <CheckCircle className="h-4 w-4" />
            <AlertDescription>
              ✅ Configuración completada exitosamente. Los productos y precios ahora tienen habilitado el cálculo automático de impuestos.
            </AlertDescription>
          </Alert>
        )}

        {configurationStatus === 'error' && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              ❌ Error: {errorMessage}
            </AlertDescription>
          </Alert>
        )}

        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Esta función creará o actualizará tus productos de Stripe con:
          </p>
          <ul className="text-sm text-muted-foreground list-disc list-inside space-y-1">
            <li>Tax behavior configurado como "exclusive"</li>
            <li>Tax code para Software as a Service (SaaS)</li>
            <li>Precios mensuales y anuales optimizados</li>
            <li>Configuración automática de impuestos</li>
          </ul>
        </div>

        <Button 
          onClick={handleConfigureStripeTax}
          disabled={isConfiguring}
          className="w-full"
        >
          {isConfiguring ? 'Configurando...' : 'Configurar Impuestos Automáticos'}
        </Button>
      </CardContent>
    </Card>
  );
};